import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import StoryblokClient, { StoryData } from 'storyblok-js-client';
import { Props } from '../types';
import { PageStateContext, StoryblokService } from '../../services';
import { Teaser, TeaserFields } from './teaser';
import { getTeaserMapping, TeaserPageTypes, TeaserPageTypesSettings } from '../../services/compMapping/teasers';
import { markupFromRichtextField } from './richtext';
import { Link } from '../../services/navigation/types';

const getStoryContentType = (componentName: string): string => Object
  .keys(TeaserPageTypesSettings)
  .find((key) => TeaserPageTypesSettings[key].component === componentName);

const parseUrl = (teaserUrl?: Link) => (
  teaserUrl?.story?.url || teaserUrl?.url || teaserUrl?.cached_url
);

export const RocheTeaser = ({ blok, getComponent, slot }: Props<TeaserFields>): JSX.Element => {
  const { settings } = useContext(PageStateContext);

  const teaserUrl = parseUrl(blok.teaser_url as Link);
  const storyTeaser = blok.teaser_url?.linktype === 'story' && teaserUrl;

  const storyblokJsClient = useRef(null);
  const [teaserProps, setTeaserProps] = useState<TeaserFields | null>(null);

  const getTeaserData = async (url: string): Promise<StoryData> => {
    const { data } = await storyblokJsClient.current?.getStory('', {
      by_slugs: url,
      resolve_relations: 'did-article-page.authors,did-article-page.categories,page-product.categories',
    });

    return data.stories[0];
  };

  useEffect(() => {
    storyblokJsClient.current = new StoryblokClient({
      accessToken: StoryblokService.getConfig().options.accessToken as string,
    });
  }, []);

  useEffect(() => {
    if (storyTeaser) {
      getTeaserData(teaserUrl)
        .then((story) => {
          if (!story) return;
          // eslint-disable-next-line max-len
          const storyType = getStoryContentType(story.content.component) as TeaserPageTypes || TeaserPageTypes.DEFAULT;
          setTeaserProps(getTeaserMapping(storyType)(story, {}, settings));
        });
    }
  }, [teaserUrl]);

  const { description, ...restBlok } = blok;

  const isDescriptionRichtext = typeof description === 'object';
  const descriptionHTML = isDescriptionRichtext
    ? markupFromRichtextField(description)
    : description;

  if (!storyTeaser || !teaserProps) {
    return <Teaser blok={{
      ...restBlok,
      description_html: descriptionHTML,
    }} getComponent={getComponent} slot={slot} />;
  }

  const { description: teaserDescription, ...teaser } = teaserProps;

  const props = {
    ...teaser,
    ...restBlok,
    headline: blok.headline || teaserProps.headline,
    description_html: descriptionHTML || teaserDescription,
    date: (blok.date || teaserProps.date),
    slotted_media: blok.slotted_media?.length === 0
      ? (teaserProps.slotted_media || [])
      : blok.slotted_media,
    slotted_link: blok.slotted_link?.length === 0
      ? (teaserProps.slotted_link || [])
      : blok.slotted_link,
  };

  return <Teaser blok={props} getComponent={getComponent} slot={slot} />;
};
